import React from "react";
import { Button as MuiButton, ButtonProps as MuiButtonProps } from "@material-ui/core";
import { Link } from "react-router-dom";

interface HvButtonProps extends Omit<MuiButtonProps, "color"> {
  variant?: "primary" | "outline" | "dark" | "outline-on-dark";
  size?: "default" | "lg";
  to?: string;
  href?: string;
}

// Thin wrapper around MUI's Button using HomeV2's own styling hooks
// instead of the app-wide .btn-primary/.btn-secondary classes, so this
// page's buttons stay self-contained.
const HvButton = ({
  variant = "primary",
  size = "default",
  to,
  href,
  className = "",
  children,
  ...props
}: HvButtonProps) => {
  const classes = `hv2-btn hv2-btn--${variant} ${size === "lg" ? "hv2-btn--lg" : ""} ${className}`;

  // Always use variant="contained" regardless of our own visual variant.
  // Both MUI's variant="text" (the default when unset) and variant="outlined"
  // pick up app-wide !important overrides elsewhere in the codebase (one
  // forces .MuiButton-text transparent, another forces a long list of
  // legacy button classes including .MuiButton-outlined to a pink
  // border/text). "contained" is the only variant with a *non*-!important
  // leaked rule, which our own hv2-btn--* classes (higher specificity,
  // scoped under .home-v2) can safely override for every visual style
  // (including the ones that are visually "outline").
  const muiVariant = "contained";

  if (to) {
    return (
      <MuiButton component={Link as any} to={to} variant={muiVariant} className={classes} disableRipple {...props}>
        {children}
      </MuiButton>
    );
  }

  if (href) {
    return (
      <MuiButton component="a" href={href} variant={muiVariant} className={classes} disableRipple {...props}>
        {children}
      </MuiButton>
    );
  }

  return (
    <MuiButton variant={muiVariant} className={classes} disableRipple {...props}>
      {children}
    </MuiButton>
  );
};

export default HvButton;
